// missionTemplateUtils.js

import { normalizeMissionTask } from 'missions/taskmodal/missionTaskModalUtils';
import { sortMissionTemplatesByConfiguredOrder } from 'utilities/MissionTemplateOrder';
import {
  assignWorkflowSpineCanvas,
  ensureWorkflowTemplateGraph,
} from 'workflows/workflowGraphUtils';

const toKeyString = (value) => String(value || '').trim();

const normalizeObject = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});

export function buildLegacyMissionTemplateKey(typeKey) {
  const normalizedTypeKey = toKeyString(typeKey);
  return normalizedTypeKey ? `mission:${normalizedTypeKey}:default` : 'mission:default';
}

export function normalizeMissionTemplateTaskList(tasks) {
  return (Array.isArray(tasks) ? tasks : [])
    .map((task) => normalizeMissionTask(task))
    .filter((task) => toKeyString(task?.key) && String(task?.title || '').trim());
}

export function normalizeMissionTemplate(template, options = {}) {
  const normalizedTemplate = normalizeObject(template);
  const fallbackKey = toKeyString(options?.fallbackKey);

  const numericPosition = Number(normalizedTemplate?.position);

  // Migrate edges from raw tasks first (may still carry prerequisiteTaskKeys).
  const graph = ensureWorkflowTemplateGraph({
    tasks: Array.isArray(normalizedTemplate?.tasks) ? normalizedTemplate.tasks : [],
    edges: normalizedTemplate?.edges,
  });

  const tasks = assignWorkflowSpineCanvas(
    normalizeMissionTemplateTaskList(graph.tasks)
  );
  const taskKeys = new Set(tasks.map((task) => toKeyString(task?.key)).filter(Boolean));
  const edges = graph.edges.filter((edge) => (
    taskKeys.has(edge.fromTaskKey)
    && (edge.toTaskKey == null || taskKeys.has(edge.toTaskKey))
  ));

  return {
    description: String(normalizedTemplate?.description || ''),
    edges,
    iconKey: toKeyString(normalizedTemplate?.iconKey) || 'account_tree',
    key: toKeyString(normalizedTemplate?.key) || fallbackKey,
    position: Number.isFinite(numericPosition) ? numericPosition : null,
    tasks,
    title: String(normalizedTemplate?.title || '').trim(),
  };
}

export function normalizeMissionTemplateList(missionsValue, options = {}) {
  const normalizedMissions = normalizeObject(missionsValue);
  const legacyTemplateKey = toKeyString(options?.legacyTemplateKey);
  const rawTemplates = Array.isArray(normalizedMissions?.templates)
    ? normalizedMissions.templates
    : [];

  if (rawTemplates.length > 0) {
    return sortMissionTemplatesByConfiguredOrder(
      rawTemplates
        .map((template) => normalizeMissionTemplate(template))
        .filter((template) => template.key)
    );
  }

  const legacyTemplate = normalizeMissionTemplate(normalizedMissions, {
    fallbackKey: legacyTemplateKey,
  });

  if (!legacyTemplate.key) return [];
  if (!legacyTemplate.title && legacyTemplate.tasks.length === 0) return [];

  return [legacyTemplate];
}

export function buildMissionTemplatesPayload(templates) {
  return {
    missions: {
      savedAtMs: Date.now(),
      templates: (Array.isArray(templates) ? templates : [])
        .map((template) => normalizeMissionTemplate(template))
        .filter((template) => template.key),
      version: 3,
    },
  };
}